/**
 * RummyBoard — Main game board showing hand, stock, discard pile, wild joker, opponents.
 */
import React from 'react';
import {
  RummyBoardProps, Card, VisibleRummyPlayerState,
  SUIT_SYMBOLS, SUIT_COLORS,
} from '../types';
import './RummyBoard.css';

const RummyCardDisplay: React.FC<{
  card: Card;
  faceDown?: boolean;
  selected?: boolean;
  onClick?: () => void;
  size?: 'small' | 'medium' | 'large';
  className?: string;
}> = ({ card, faceDown, selected, onClick, size = 'medium', className = '' }) => {
  if (faceDown) {
    return (
      <div
        className={`rummy-card rummy-card--face-down rummy-card--${size} ${className}`}
        onClick={onClick}
        role="button"
        tabIndex={0}
        aria-label="Face-down card"
        onKeyDown={e => e.key === 'Enter' && onClick?.()}
      >
        <span className="rummy-card__back">🂠</span>
      </div>
    );
  }

  if (card.isJoker) {
    return (
      <div
        className={`rummy-card rummy-card--joker rummy-card--${size} ${selected ? 'rummy-card--selected' : ''} ${className}`}
        onClick={onClick}
        role="button"
        tabIndex={0}
        aria-label="Joker card"
        onKeyDown={e => e.key === 'Enter' && onClick?.()}
      >
        <span className="rummy-card__joker-icon">🃏</span>
        <span className="rummy-card__joker-text">JKR</span>
      </div>
    );
  }

  const color = SUIT_COLORS[card.suit];
  const symbol = SUIT_SYMBOLS[card.suit];

  return (
    <div
      className={`rummy-card rummy-card--${color} rummy-card--${size} ${selected ? 'rummy-card--selected' : ''} ${card.isWildJoker ? 'rummy-card--wild' : ''} ${className}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      aria-label={`${card.rank} of ${card.suit}${card.isWildJoker ? ' (wild joker)' : ''}`}
      onKeyDown={e => e.key === 'Enter' && onClick?.()}
    >
      <span className="rummy-card__rank">{card.rank}</span>
      <span className="rummy-card__suit">{symbol}</span>
      {card.isWildJoker && <span className="rummy-card__wild-badge">W</span>}
    </div>
  );
};

const OpponentDisplay: React.FC<{
  player: VisibleRummyPlayerState;
  isCurrentTurn: boolean;
}> = ({ player, isCurrentTurn }) => (
  <div className={`rummy-board__opponent ${isCurrentTurn ? 'rummy-board__opponent--active' : ''} ${player.status !== 'active' ? 'rummy-board__opponent--inactive' : ''}`}>
    <div className="rummy-board__opponent-name">{player.name}</div>
    <div className="rummy-board__opponent-cards">
      {player.status === 'active' ? (
        <span className="rummy-board__opponent-count">🂠 ×{player.cardCount}</span>
      ) : player.status === 'declared' ? (
        <span className="rummy-board__opponent-status">✅ Declared</span>
      ) : (
        <span className="rummy-board__opponent-status">❌ Dropped</span>
      )}
    </div>
    <div className="rummy-board__opponent-score">Score: {player.score}</div>
  </div>
);

const RummyBoard: React.FC<RummyBoardProps> = ({
  gameState,
  currentPlayerId,
  selectedCards,
  onCardSelect,
  onDrawStock,
  onDrawDiscard,
  onDiscard,
  onDeclare,
  onSort,
  onDrop,
  disabled,
}) => {
  const humanPlayer = gameState.players.find(p => p.id === currentPlayerId);
  const opponents = gameState.players.filter(p => p.id !== currentPlayerId);
  const isMyTurn = gameState.players[gameState.currentPlayerIndex]?.id === currentPlayerId;
  const isDrawPhase = gameState.turnPhase === 'draw';
  const isDiscardPhase = gameState.turnPhase === 'discard';

  const handleDiscardSelected = () => {
    const selectedId = Array.from(selectedCards)[0];
    if (selectedId) {
      onDiscard(selectedId);
    }
  };

  return (
    <div className="rummy-board" role="region" aria-label="Rummy game board">
      {/* Opponents */}
      <div className="rummy-board__opponents">
        {opponents.map((opp, idx) => (
          <OpponentDisplay
            key={opp.id}
            player={opp}
            isCurrentTurn={gameState.players[gameState.currentPlayerIndex]?.id === opp.id}
          />
        ))}
      </div>

      {/* Center area: stock, discard, wild joker */}
      <div className="rummy-board__center">
        {/* Stock pile */}
        <div className="rummy-board__pile rummy-board__stock">
          <button
            className="rummy-board__pile-btn"
            onClick={onDrawStock}
            disabled={disabled || !isMyTurn || !isDrawPhase}
            aria-label={`Draw from stock (${gameState.stockCount} cards remaining)`}
          >
            <div className="rummy-card rummy-card--face-down rummy-card--medium">
              <span className="rummy-card__back">🂠</span>
            </div>
            <span className="rummy-board__pile-label">Stock ({gameState.stockCount})</span>
          </button>
        </div>

        {/* Discard pile */}
        <div className="rummy-board__pile rummy-board__discard">
          <button
            className="rummy-board__pile-btn"
            onClick={onDrawDiscard}
            disabled={disabled || !isMyTurn || !isDrawPhase || !gameState.discardTop}
            aria-label={gameState.discardTop ? `Draw ${gameState.discardTop.rank} of ${gameState.discardTop.suit} from discard pile` : 'Discard pile empty'}
          >
            {gameState.discardTop ? (
              <RummyCardDisplay card={gameState.discardTop} size="medium" />
            ) : (
              <div className="rummy-card rummy-card--empty rummy-card--medium">
                <span>Empty</span>
              </div>
            )}
            <span className="rummy-board__pile-label">Discard</span>
          </button>
        </div>

        {/* Wild joker indicator */}
        {gameState.wildJoker && (
          <div className="rummy-board__wild-joker">
            <span className="rummy-board__wild-label">Wild Joker</span>
            <RummyCardDisplay card={gameState.wildJoker} size="small" className="rummy-board__wild-card" />
          </div>
        )}
      </div>

      {/* Turn indicator */}
      <div className="rummy-board__turn-indicator" role="status" aria-live="polite">
        {isMyTurn ? (
          <span className="rummy-board__your-turn">
            {isDrawPhase ? '🎯 Your Turn — Draw a card' : '🎯 Your Turn — Discard a card'}
          </span>
        ) : (
          <span className="rummy-board__waiting">
            ⏳ Waiting for {gameState.players[gameState.currentPlayerIndex]?.name}...
          </span>
        )}
      </div>

      {/* Player's hand */}
      {humanPlayer?.hand && (
        <div className="rummy-board__hand-area">
          <div className="rummy-board__hand" role="list" aria-label="Your hand">
            {humanPlayer.hand.map(card => (
              <RummyCardDisplay
                key={card.id}
                card={card}
                selected={selectedCards.has(card.id)}
                onClick={() => onCardSelect(card.id)}
                size="large"
                className="rummy-board__hand-card"
              />
            ))}
          </div>

          {/* Action buttons */}
          <div className="rummy-board__actions">
            <button
              className="rummy-board__action-btn rummy-board__action-btn--sort"
              onClick={onSort}
              disabled={disabled}
              aria-label="Sort hand"
            >
              🔀 Sort
            </button>

            {isMyTurn && isDiscardPhase && selectedCards.size === 1 && (
              <button
                className="rummy-board__action-btn rummy-board__action-btn--discard"
                onClick={handleDiscardSelected}
                disabled={disabled}
                aria-label="Discard selected card"
              >
                🗑️ Discard
              </button>
            )}

            {isMyTurn && isDiscardPhase && humanPlayer.hand.length === 14 && (
              <button
                className="rummy-board__action-btn rummy-board__action-btn--declare"
                onClick={onDeclare}
                disabled={disabled}
                aria-label="Declare and show melds"
              >
                ✅ Declare
              </button>
            )}

            <button
              className="rummy-board__action-btn rummy-board__action-btn--drop"
              onClick={onDrop}
              disabled={disabled || !isMyTurn}
              aria-label="Drop from round"
            >
              🚪 Drop
            </button>
          </div>
        </div>
      )}

      {/* Melds display (after declaration) */}
      {humanPlayer?.melds && humanPlayer.melds.length > 0 && (
        <div className="rummy-board__melds" role="region" aria-label="Your melds">
          <h3 className="rummy-board__melds-title">Your Melds</h3>
          <div className="rummy-board__melds-list">
            {humanPlayer.melds.map((meld, idx) => (
              <div key={idx} className={`rummy-board__meld rummy-board__meld--${meld.type} ${meld.isPure ? 'rummy-board__meld--pure' : ''}`}>
                <span className="rummy-board__meld-label">
                  {meld.type === 'sequence' ? (meld.isPure ? 'Pure Seq' : 'Impure Seq') : 'Set'}
                </span>
                <div className="rummy-board__meld-cards">
                  {meld.cards.map(card => (
                    <RummyCardDisplay key={card.id} card={card} size="small" />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export { RummyBoard, RummyCardDisplay };
export default RummyBoard;
